import {
	extractNumber,
	extractPlayers,
	extractSelection,
	getPlayerStats,
	resolveCondition,
} from '../parse-market.utils';
import { GameContext, InPlay, ScopeCompletedResolver } from '../../interfaces';
import { MarketOutcome, PeriodType } from '../../enums';

export const mlbScopeResolver: ScopeCompletedResolver = (period, { isCompleted, inPlay }) => {
	if (isCompleted) return true;

	const currentPeriod = Number(inPlay?.period ?? 0);
	const outs = inPlay?.outs ?? 0;

	if (!currentPeriod) return false;

	let endPeriod = 0;

	switch (period) {
		case PeriodType.Game:
			return isCompleted;

		case PeriodType.Half1: {
			endPeriod = 4;
			break;
		}

		case PeriodType.Half2: {
			endPeriod = 9;
			break;
		}

		case PeriodType.FirstPeriod3:
			endPeriod = 3;
			break;

		case PeriodType.FirstPeriod7:
			endPeriod = 7;
			break;

		default: {
			const periodNumber = Number(period.replace('period_', ''));

			if (!isNaN(periodNumber)) {
				endPeriod = periodNumber;
			} else {
				return false;
			}
			break;
		}
	}

	return currentPeriod > endPeriod || outs === 3;
};

export const resolveGamePlayerProp = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string,
	extractAfter: string[],
	thresholdValue?: number
): [MarketOutcome, number?] => {
	const threshold = thresholdValue ?? extractNumber(q);
	const playerName = extractSelection(q, 'Will ', extractAfter);
	const playerResult = getPlayerStats(playerName, r);

	const value = playerResult?.[statKey] ?? 0;

	return [resolveCondition(value >= threshold, gc.isCompleted), value];
};

export const resolveGamePlayerPropCombo = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string
): [MarketOutcome, number?] => {
	const threshold = extractNumber(q);
	const players = extractPlayers(q);
	const total = players.reduce((sum, player) => {
		const stats = getPlayerStats(player, r);

		return sum + (stats?.[statKey] ?? 0);
	}, 0);

	return [resolveCondition(total >= threshold, gc.isCompleted), total];
};

export const mlbPeriodTextMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Game]: '',
	[PeriodType.Half1]: ' in the first half of the game',
	[PeriodType.Period1]: ' in the 1st inning',
	[PeriodType.Period2]: ' in the 2nd inning',
	[PeriodType.Period3]: ' in the 3rd inning',
	[PeriodType.Period4]: ' in the 4th inning',
	[PeriodType.Period5]: ' in the 5th inning',
	[PeriodType.Period6]: ' in the 6th inning',
	[PeriodType.Period7]: ' in the 7th inning',
	[PeriodType.Period8]: ' in the 8th inning',
	[PeriodType.Period9]: ' in the 9th inning',
	[PeriodType.FirstPeriod3]: ' in the first 3 innings',
	[PeriodType.FirstPeriod7]: ' in the first 7 innings',
};

export const buildMoneylineQuestion = (team: string, period: PeriodType) => {
	const base = `Will ${team} win`;
	const suffix = mlbPeriodTextMap[period] ?? '';

	return period === PeriodType.Game ? `${base} tonight?` : `${base}${suffix}?`;
};

export const buildRunLineQuestion = (team: string, points: number, period: PeriodType): string => {
	const pts = Math.abs(points);
	const suffix = mlbPeriodTextMap[period];

	if (points < 0) {
		return `Will ${team} win by more than ${pts} runs${suffix}?`;
	}

	return `Will ${team} avoid losing by more than ${pts} runs${suffix}?`;
};

export const buildTeamTotalQuestion = (
	team: string,
	points: number,
	period: PeriodType
): string => {
	const pts = Math.abs(points);
	const suffix = mlbPeriodTextMap[period];

	return `Will ${team} score at least ${pts} runs${suffix}?`;
};

export const buildTotalRunsQuestion = (points: number, period: PeriodType): string => {
	const pts = Math.abs(points);
	const suffix = mlbPeriodTextMap[period];

	return `Will there be at least ${pts} runs${suffix}?`;
};

export const buildTotalRunsOddEvenQuestion = (period: PeriodType): string => {
	const suffix = mlbPeriodTextMap[period];

	return `Will the total runs${suffix} be even?`;
};

export const buildFirstTeamToScoreQuestion = (team: string, period: PeriodType): string => {
	if (period === PeriodType.Game) {
		return `Will ${team} score first?`;
	}

	const suffix = mlbPeriodTextMap[period] ?? '';

	return `Will ${team} score${suffix}?`;
};

export const getPeriodScores = (
	gc: GameContext,
	period: PeriodType
): { home: number; away: number; isCompleted: boolean } => {
	const { home: scoresHome, away: scoresAway } = gc.periodScores;

	if (period === PeriodType.Game) {
		return { home: gc.homeScore, away: gc.awayScore, isCompleted: gc.isCompleted };
	}

	if (period === PeriodType.Half1) {
		const home = [1, 2, 3, 4].reduce((sum, i) => sum + (scoresHome[`period_${i}`] || 0), 0);
		const away = [1, 2, 3, 4].reduce((sum, i) => sum + (scoresAway[`period_${i}`] || 0), 0);

		return { home, away, isCompleted: gc.isScopeCompleted(period) };
	}

	if (period.startsWith('first_period_')) {
		const maxPeriod = Number(period.replace('first_period_', ''));
		const home = Array.from(
			{ length: maxPeriod },
			(_, i) => scoresHome[`period_${i + 1}`] || 0
		).reduce((a, b) => a + b, 0);
		const away = Array.from(
			{ length: maxPeriod },
			(_, i) => scoresAway[`period_${i + 1}`] || 0
		).reduce((a, b) => a + b, 0);

		return { home, away, isCompleted: gc.isScopeCompleted(period) };
	}

	if (period.startsWith('period_')) {
		const key = period as keyof typeof scoresHome;

		return {
			home: scoresHome[key] ?? 0,
			away: scoresAway[key] ?? 0,
			isCompleted: gc.isScopeCompleted(period),
		};
	}

	return { home: 0, away: 0, isCompleted: false };
};

const REG_INNINGS = 9;

export const mlbTimeProgressResolver = (inPlay: InPlay): number | null => {
	const period = Number(inPlay?.period);
	const clock = inPlay?.clock;
	const outs = inPlay?.outs ?? 0;
	if (!period || !clock) return null;

	const totalHalf = Math.max(REG_INNINGS, period) * 2;
	const elapsed = (period - 1) * 2 + (clock === 'Bottom' ? 1 : 0) + outs / 3;

	return elapsed / totalHalf;
};
